"use client";

import { motion } from "framer-motion";
import { ArrowRight, BedDouble, CalendarDays, MapPin, Route } from "lucide-react";
import { Button } from "@/components/ui/button";
const priorities = [{
  id: "selected-periods",
  title: "Selected Periods",
  description: <>S<span className="!font-semibold">upport weekday, shoulder-season or specific visitor periods that matter to your course.</span></>,
  icon: CalendarDays,
  imageUrl: "https://ideal-jay.10web.cloud/wp-content/uploads/2026/07/selected-periods-plain.png?v=ismceoq9k0p"
}, {
  id: "stay-and-play",
  title: "Stay & Play",
  description: <><span className="!font-semibold">Present approved golf-and-accommodation offers within a clearer traveller journey.</span></>,
  icon: BedDouble,
  imageUrl: "https://ideal-jay.10web.cloud/wp-content/uploads/2026/07/stay-and-play-plain.png?v=5wr80rp0pac"
}, {
  id: "destination-visibility",
  title: "Destination Visibility",
  description: <><span className="!font-semibold">Position your course within relevant destination stories, collections and golf guides.</span></>,
  icon: MapPin,
  imageUrl: "https://ideal-jay.10web.cloud/wp-content/uploads/2026/07/destination-visibility-plain.png?v=j6cm94339ib"
}, {
  id: "future-golf-travel",
  title: "Future Golf Travel",
  description: <><span className="!font-semibold">Explore future itinerary, package and supplier collaboration with TeeListClub.</span></>,
  icon: Route,
  imageUrl: "https://ideal-jay.10web.cloud/wp-content/uploads/2026/07/future-golf-travel-plain.png?v=wbfe4sinn08"
}];
export default function BusinessPrioritiesGrid() {
  const scrollToEnquiry = () => {
    document.getElementById("partner-enquiry")?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  };
  return <section id="business-priorities" aria-labelledby="business-priorities-heading" className="relative w-full overflow-hidden bg-[#F7F3E9] ![padding:40px_32px_40px_32px] !h-[580px]" style={{
    fontFamily: '"Raleway", Arial, sans-serif'
  }}>
      {}
      <div aria-hidden="true" className={["pointer-events-none absolute inset-0", "bg-[radial-gradient(circle_at_14%_50%,rgba(183,154,95,0.10),transparent_29%),radial-gradient(circle_at_88%_24%,rgba(191,255,0,0.06),transparent_25%)]"].join(" ")} />

      <div className={["relative z-10 mx-auto grid w-full", "max-w-[1480px]", "grid-cols-1 items-center", "gap-11", "lg:grid-cols-[minmax(330px,0.76fr)_minmax(0,1.24fr)]", "lg:gap-14", "xl:gap-20"].join(" ")}>
        {}
        <motion.div initial={{
        opacity: 0,
        x: -22
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.55,
        ease: "easeOut"
      }} className="max-w-[500px]">
          <p className="font-bold uppercase tracking-[0.2em] text-[#9A793A] !text-xs">
            Partnership Opportunities
          </p>

          <h2 id="business-priorities-heading" className={["mt-4 p-0", "text-[40px] font-medium", "leading-[1.05]", "tracking-[-0.035em]", "text-[#073F31]", "md:text-[48px]", "lg:text-[54px]"].join(" ")} style={{
          fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif'
        }}>
            Built around your business priorities.
          </h2>

          <p className={["mt-6 max-w-[480px]", "text-[15px]", "leading-[1.72]", "text-[#596B63]", "md:text-[16px]"].join(" ")}>
            Whether your focus is a particular season, visitor period,
            destination or future travel opportunity, we shape the partnership
            around what matters to your course.
          </p>

          <div className={["mt-7 border-l-2", "border-[#B79A5F]", "pl-5"].join(" ")}>
            <p className={["max-w-[410px]", "text-[14px] font-medium", "leading-[1.65]", "text-[#29483E]"].join(" ")}>
              Start with one clear priority. The partnership can grow from
              there.
            </p>
          </div>

          <Button type="button" onClick={scrollToEnquiry} className="h-9 inline-flex items-center justify-center gap-2 whitespace-nowrap [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive py-2 has-[>svg]:px-3 group mt-8 min-w-[205px] rounded-[9px] bg-[#BFFF00] px-6 text-[11px] font-bold uppercase tracking-[0.075em] text-[#073F31] shadow-[0_9px_24px_rgba(7,63,49,0.14)] transition-all duration-300 !h-[48px] !w-[280px] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:-translate-y-[2px] hover:bg-[#CBFF36] hover:shadow-[0_13px_28px_rgba(7,63,49,0.18)]">
            Discuss Your Priority

            <ArrowRight className={["ml-2 size-4", "transition-transform duration-200", "group-hover:translate-x-1"].join(" ")} strokeWidth={1.7} aria-hidden="true" />
          </Button>
        </motion.div>

        {}
        <div className="grid grid-cols-1 gap-4 !w-[900px] !h-[500px] sm:grid-cols-2">
 {priorities.map((priority, index) => {
          const Icon = priority.icon;
          const isFuture = priority.title === "Future Golf Travel";
          return <motion.article key={priority.title} initial={{
            opacity: 0,
            y: 22
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.5,
            delay: index * 0.07,
            ease: "easeOut"
          }} className={["group relative", "min-h-[225px]", "overflow-hidden", "rounded-[15px]", "bg-[#073F31]", "transition-all duration-300", "hover:-translate-y-[3px]", "md:min-h-[235px]", isFuture ? ["border border-[#BFFF00]/65", "shadow-[0_16px_36px_rgba(191,255,0,0.12)]", "hover:shadow-[0_20px_42px_rgba(191,255,0,0.18)]"].join(" ") : ["border border-[#DED3BF]/80", "shadow-[0_11px_28px_rgba(7,63,49,0.10)]", "hover:shadow-[0_17px_34px_rgba(7,63,49,0.16)]"].join(" ")].join(" ")}>
      <img src={priority.imageUrl} alt="" aria-hidden="true" loading="lazy" decoding="async" onError={event => {
              event.currentTarget.style.display = "none";
            }} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.035] !bg-[rgba(53,82,20,1)]" />

      {}
      <div aria-hidden="true" className={["pointer-events-none absolute inset-0", isFuture ? "bg-[linear-gradient(180deg,rgba(7,63,49,0.02)_5%,rgba(7,63,49,0.25)_42%,rgba(3,39,31,0.96)_100%)]" : "bg-[linear-gradient(180deg,rgba(7,63,49,0.02)_8%,rgba(7,63,49,0.18)_45%,rgba(3,39,31,0.92)_100%)]"].join(" ")} />

      {}
      {isFuture && <>
          <div aria-hidden="true" className={["pointer-events-none absolute inset-x-0 top-0 h-[3px]", "bg-[linear-gradient(90deg,transparent,#BFFF00,transparent)]"].join(" ")} />

          <span className={["absolute right-4 top-4 z-20", "rounded-full", "border border-[#BFFF00]/45", "bg-[#073F31]/78", "px-3 py-1", "text-[9px] font-bold uppercase", "tracking-[0.16em]", "text-[#BFFF00]", "backdrop-blur-[8px]"].join(" ")}>
            Future
          </span>
        </>}

      <div className="relative z-10 flex min-h-[225px] flex-col justify-end p-5 !bg-[rgba(0,0,0,0.17)] md:min-h-[235px] md:p-6">
        <div className="flex size-[38px] items-center justify-center backdrop-blur-[8px] border border-[#D8C69B]/45 text-[#D8C69B] !bg-[rgba(0,0,0,0.41)] !p-0 ![margin:-10px_0px_-10px_0px]">
          <Icon className="lucide lucide-bed-double lucide lucide-bed-double size-[18px] ![margin:-50px_0px_-50px_0px]" strokeWidth={1.5} aria-hidden="true" />
        </div>

        <h3 className={["tlc-priority-card-title", "mt-4", "text-[21px]", "font-bold", "leading-[1.12]", isFuture ? "text-[#DFFF76]" : "text-[#FFFDF7]"].join(" ")}>
          {priority.title}
        </h3>

        <p className={["tlc-priority-card-copy", "mt-2 max-w-[440px]", "text-[12px]", "leading-[1.55]", "text-[#FFFDF7]/88", "md:text-[13px]"].join(" ")}>
          {priority.description}
        </p>
      </div>
    </motion.article>;
        })}
        </div>
      </div>
    </section>;
}